import { useQueryClient } from '@tanstack/react-query'

import { useQuery } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { logger } from '@percent/admin-dashboard/common/utility/logger'

// The domains an account or group holds roles in. Eventual until one of this
// subject's roles is changed: a strong read skips OpenFGA's check cache, and
// most opens follow no change. For a while after a change every read is strong,
// so it reads the change back rather than what OpenFGA had cached. The mark
// lasts the query cache's gcTime (5 minutes from the first change), well past
// OpenFGA's cache controller window (20s), after which eventual is fresh again.
export function useAssignedDomains(subject: 'accounts' | 'groups', id: string | undefined, enabled = true) {
  const { iamService } = useServices()
  const queryClient = useQueryClient()
  // Kept in the query cache rather than the component, so every view of this
  // subject (the drawer, the member profile) reads strongly after a change made
  // in any of them, and it is checked when a request is made, so a refetch
  // called straight after a change is already strong.
  const changedKey = ['assignedDomainsChanged', subject, id]

  const { data, isLoading, refetch } = useQuery(
    ['assignedDomains', subject, id],
    () => {
      const consistencyLevel = queryClient.getQueryData(changedKey) ? 'strong' : 'eventual'

      return subject === 'accounts'
        ? iamService.getAssignedDomains({ accountId: id ?? '', consistencyLevel })
        : iamService.getGroupAssignedDomains({ groupId: id ?? '', consistencyLevel })
    },
    undefined,
    { enabled: enabled && !!id }
  )

  const markChanged = () => {
    if (id) queryClient.setQueryData(changedKey, true)
  }

  return {
    assignedDomains: data ?? undefined,
    isLoading,
    // A change the assign/unassign response already shows: later reads are strong, nothing is fetched now
    markChanged,
    // A change that reaches beyond its own domain: read the whole list again
    refresh: () => {
      if (!id) return
      markChanged()
      refetch().catch(logger)
    }
  }
}
